import clsx from 'clsx';
import {
  useEffect,
  useMemo,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type MouseEventHandler as ReactMouseEventHandler,
} from 'react';
import { useIntl } from 'react-intl';

import { InlineButton } from '../../buttons/inline-button/InlineButton.js';
import { useContainerSize } from '../../core/hooks/useContainerSize.js';
import { DataTestId } from '../../core/types/data-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { COMPONENT_NAME } from './chip-group-constants.js';
import * as styles from './Control.sty.js';
import { useChipGroupContext } from './hooks/useChipGroupContext.js';
import { useInternalChipGroupContext } from './hooks/useInternalChipGroupContext.js';
import { ChipGroupActions } from './state/chip-group-state-reducer.js';

//#region Types

/**
 * @public
 */
export interface ChipGroupControlProps
  extends WithChildren, StylingProps, DataTestId {
  /**
   * Whether to show more chips or collapse chips.
   * If undefined, the action is inferred.
   * If no more items are available and all items fit in one line,
   * the control is automatically hidden unless display is set.
   * @defaultValue inferred from the `expanded` / `defaultExpanded` prop of the `ChipGroup`
   */
  display?: 'more' | 'less';

  /**
   * Whether to render `Show more` or `Show x more` by default.
   * If true, the count is calculated automatically. The count can be overridden
   * by passing a number.
   * @defaultValue undefined
   */
  count?: boolean | number;

  /**
   * Whether the control should show the loading indicator.
   * @defaultValue false
   */
  loading?: boolean;

  /** Callback triggered when the control is interacted with. */
  onClick?: ReactMouseEventHandler<HTMLButtonElement>;
}
//#endregion

/**
 * @public
 */
export const Control = (props: ChipGroupControlProps) => {
  const {
    display,
    count,
    loading = false,
    onClick,
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
  }: ChipGroupControlProps = props;
  const intl = useIntl();

  const { hiddenKeys, visibleKeys } = useChipGroupContext();
  const internalChipGroupContext = useInternalChipGroupContext();
  const {
    chipGroupId,
    expanded,
    allChipsFit,
    setExpanded,
    dispatch,
    setPendingFocusChip,
  } = internalChipGroupContext || {};

  const controlRef = useRef<HTMLButtonElement | null>(null);
  const { width: controlWidth } = useContainerSize(controlRef);
  const width = Math.ceil(controlWidth);

  const label = useMemo(() => {
    const moreOrLess = expanded && display !== 'more' ? 'less' : 'more';
    const hiddenChipsCount =
      count === true && hiddenKeys.length > 0 ? hiddenKeys.length : count;
    const showCount =
      typeof hiddenChipsCount === 'number' && moreOrLess === 'more';

    return intl.formatMessage(
      {
        defaultMessage: 'Show {count} {moreOrLess}',
        id: 'ARdI7d8cJxHnUayP',
        description: 'Label for the expand / collapse button.',
      },
      {
        moreOrLess: expanded && display !== 'more' ? 'less' : 'more',
        count: showCount ? hiddenChipsCount : undefined,
      },
    );
  }, [count, display, expanded, hiddenKeys, intl]);

  useEffect(() => {
    dispatch?.({
      type: ChipGroupActions.ADDCONTROL,
      payload: { width },
    });

    return () => {
      dispatch?.({
        type: ChipGroupActions.REMOVECONTROL,
        payload: {},
      });
    };
    // only for mount and unmount
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch?.({
      type: ChipGroupActions.UPDATECONTROL,
      payload: {
        width: width === 0 ? undefined : width,
        show: display !== undefined ? 'always' : 'auto',
      },
    });
  }, [dispatch, display, width]);

  const shouldHide =
    display === undefined &&
    (allChipsFit || visibleKeys.length === 0) &&
    !loading;

  if (shouldHide) {
    return null;
  }

  const isShowingLess = expanded && display !== 'more';

  return (
    <InlineButton
      as="button"
      ref={controlRef}
      data-testid={dataTestId}
      data-dt-children-name="ShowMoreLess"
      data-dt-properties={isShowingLess ? 'state:less' : 'state:more'}
      loading={loading}
      onClick={(event) => {
        onClick?.(event as ReactMouseEvent<HTMLButtonElement>);
        const isExpanding = display === 'more' || !expanded;
        const firstHiddenKey = hiddenKeys[0];
        if (isExpanding && firstHiddenKey !== undefined) {
          setPendingFocusChip?.({
            uniqueId: firstHiddenKey,
            // Use :focus-visible to detect input modality — event.detail is unreliable with NVDA (synthesizes pointer clicks via MSAA).
            showFocusRing:
              controlRef.current?.matches(':focus-visible') ?? false,
          });
        }
        setExpanded?.((prev) => (display === 'more' ? true : !prev));
      }}
      aria-controls={chipGroupId}
      aria-expanded={expanded}
      className={clsx(styles.expandButton, consumerClassName)}
      style={consumerStyle}
      data-dt-component={COMPONENT_NAME}
    >
      {children ?? label}
    </InlineButton>
  );
};
